import { useCallback, useMemo, useState } from 'react';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { CLUB_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { AcademyScheduleCalendar } from '@/components/schedule/AcademyScheduleCalendar';
import { CalendarFiltersDropdown } from '@/components/schedule/CalendarFiltersDropdown';
import { DayCourtScheduleView } from '@/components/schedule/DayCourtScheduleView';
import { ScheduleSourceTypeFilterBar } from '@/components/schedule/ScheduleSourceTypeFilterBar';
import {
  LessonCalendarMonthNav,
  LessonScheduleSubheaderToolbar,
  ScheduleDayNav,
  ScheduleWeekNav,
} from '@/components/schedule/ScheduleToolbar';
import { Button } from '@/components/ui';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useFacilityScheduleLayer } from '@/hooks/useFacilityScheduleLayer';
import { useScheduleSourceTypeAccess } from '@/hooks/useScheduleSourceTypeAccess';
import { useScheduleSourceTypeFilters } from '@/hooks/useScheduleSourceTypeFilters';
import { PlayEventScheduleDetailDrawer } from '@/components/play/PlayEventScheduleDetailDrawer';
import { formatTimeHHMM } from '@/utils/timeFormat';
import type { ScheduleOccurrence } from '@/utils/scheduleOccurrences';
import { isScheduleOccurrenceInDateRange } from '@/utils/scheduleOccurrences';
import type { OnboardingEmbeddedPageProps } from '@/components/onboarding/onboardingEmbeddedPage';

type ScheduleViewMode = 'list' | 'monthly' | 'weekly' | 'day';

const VIEW_STORAGE_KEY = 'courtsync.clubManagement.scheduleView';

function readStoredView(): ScheduleViewMode {
  if (typeof window === 'undefined') return 'day';
  const stored = localStorage.getItem(VIEW_STORAGE_KEY);
  if (stored === 'monthly' || stored === 'weekly' || stored === 'day' || stored === 'list') {
    return stored;
  }
  return 'day';
}

function ScheduleViewDropdown({
  value,
  onChange,
}: {
  value: ScheduleViewMode;
  onChange: (value: ScheduleViewMode) => void;
}) {
  const options: { value: ScheduleViewMode; label: string }[] = [
    { value: 'list', label: 'List' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'day', label: 'Day' },
  ];
  return (
    <div id={CLUB_PAGE_TOUR_STEP_IDS.CALENDAR_VIEW} className="flex items-center gap-1">
      {options.map((opt) => (
        <Button
          key={opt.value}
          type="button"
          size="sm"
          variant={value === opt.value ? 'default' : 'outline'}
          onClick={() => {
            localStorage.setItem(VIEW_STORAGE_KEY, opt.value);
            onChange(opt.value);
          }}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );
}

export default function ClubOnlyManagementCalendar({
  embedded,
}: OnboardingEmbeddedPageProps) {
  const { currentClub, isLoading: clubLoading } = useCurrentClub();
  const sourceAccess = useScheduleSourceTypeAccess();
  const { activeSourceFilters, toggleSource, isSourceActive } = useScheduleSourceTypeFilters(
    sourceAccess.availableSourceTypes
  );

  const [viewMode, setViewMode] = useState<ScheduleViewMode>(readStoredView);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [calendarWeekStart, setCalendarWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedDayDate, setSelectedDayDate] = useState(() => new Date());
  const [playEventDetailOccurrence, setPlayEventDetailOccurrence] = useState<ScheduleOccurrence | null>(
    null
  );

  const handleScheduleOccurrenceClick = useCallback((occurrence: ScheduleOccurrence) => {
    if (occurrence.sourceType === 'play_event') {
      setPlayEventDetailOccurrence(occurrence);
    }
  }, []);

  const facilityRange = useMemo(() => {
    if (viewMode === 'day') {
      return { start: startOfDay(selectedDayDate), end: endOfDay(selectedDayDate) };
    }
    if (viewMode === 'weekly') {
      return {
        start: calendarWeekStart,
        end: endOfDay(addDays(calendarWeekStart, 6)),
      };
    }
    return {
      start: startOfWeek(startOfMonth(calendarMonth)),
      end: endOfWeek(endOfMonth(calendarMonth)),
    };
  }, [viewMode, selectedDayDate, calendarWeekStart, calendarMonth]);

  const facilityLayer = useFacilityScheduleLayer({
    clubId: currentClub?.id,
    rangeStart: facilityRange.start,
    rangeEnd: facilityRange.end,
    includeCourtHire: sourceAccess.canShowCourtHire,
    includeClosures: sourceAccess.canShowClosures,
    includeOccupancyLessons: true,
    includePlayEvents: sourceAccess.canShowPlayEvents,
    organizationId: currentClub?.organizationId,
    enabled:
      (sourceAccess.hasFacilityLayer || sourceAccess.canShowPlayEvents) && !!currentClub?.id,
  });

  const courtNamesById = useMemo(
    () => Object.fromEntries(facilityLayer.clubCourts.map((c) => [c.id, c.name] as const)),
    [facilityLayer.clubCourts]
  );

  const allOccurrences = useMemo(() => {
    const merged = facilityLayer.facilityOccurrences;
    if (!activeSourceFilters.length) return merged;
    const allowed = new Set(activeSourceFilters);
    return merged.filter((occ) => allowed.has(occ.sourceType));
  }, [facilityLayer.facilityOccurrences, activeSourceFilters]);

  const dayOccurrences = useMemo(
    () => allOccurrences.filter((occ) => isSameDay(occ.date, selectedDayDate)),
    [allOccurrences, selectedDayDate]
  );

  const listCourtHire = useMemo(
    () =>
      isSourceActive('court_hire')
        ? facilityLayer.courtHireOccurrences.filter((occ) =>
            isScheduleOccurrenceInDateRange(occ, facilityRange.start, facilityRange.end)
          )
        : [],
    [facilityLayer.courtHireOccurrences, facilityRange, isSourceActive]
  );

  const listClosures = useMemo(
    () =>
      isSourceActive('closure')
        ? facilityLayer.closureOccurrences.filter((occ) =>
            isScheduleOccurrenceInDateRange(occ, facilityRange.start, facilityRange.end)
          )
        : [],
    [facilityLayer.closureOccurrences, facilityRange, isSourceActive]
  );

  const listPlayEvents = useMemo(
    () =>
      isSourceActive('play_event')
        ? facilityLayer.playEventOccurrences.filter((occ) =>
            isScheduleOccurrenceInDateRange(occ, facilityRange.start, facilityRange.end)
          )
        : [],
    [facilityLayer.playEventOccurrences, facilityRange, isSourceActive]
  );

  const handleDayDateChange = useCallback((nextDate: Date) => {
    setSelectedDayDate(nextDate);
  }, []);

  const hasActiveSourceTypeFilter =
    sourceAccess.availableSourceTypes.length > 1 &&
    activeSourceFilters.length < sourceAccess.availableSourceTypes.length;

  const calendarTourReady = Boolean(currentClub?.id) && !clubLoading && !facilityLayer.loading && !embedded;
  useAutoPageTour('club-calendar', calendarTourReady);

  const headerSubheader = (
    <LessonScheduleSubheaderToolbar
      filterSlot={
        sourceAccess.availableSourceTypes.length > 1 ? (
          <div id={CLUB_PAGE_TOUR_STEP_IDS.CALENDAR_FILTERS}>
          <CalendarFiltersDropdown
            hasActiveFilters={hasActiveSourceTypeFilter}
            panelLabel="Calendar filters"
          >
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Calendar filters</h3>
              <ScheduleSourceTypeFilterBar
                availableSourceTypes={sourceAccess.availableSourceTypes}
                activeSourceFilters={activeSourceFilters}
                onToggle={toggleSource}
                label="Show on calendar"
              />
            </div>
          </CalendarFiltersDropdown>
          </div>
        ) : (
          <div id={CLUB_PAGE_TOUR_STEP_IDS.CALENDAR_FILTERS} className="hidden" aria-hidden />
        )
      }
      lessonCount={{ filtered: allOccurrences.length, total: facilityLayer.facilityOccurrences.length }}
      calendarNav={
        viewMode === 'monthly' ? (
          <LessonCalendarMonthNav
            currentMonth={calendarMonth}
            onCurrentMonthChange={setCalendarMonth}
          />
        ) : viewMode === 'weekly' ? (
          <ScheduleWeekNav
            weekStart={calendarWeekStart}
            onWeekStartChange={setCalendarWeekStart}
          />
        ) : viewMode === 'day' ? (
          <ScheduleDayNav selectedDate={selectedDayDate} onDateChange={handleDayDateChange} />
        ) : null
      }
    />
  );

  if (clubLoading || !currentClub) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  const extraOccurrences = [
    ...facilityLayer.courtHireOccurrences,
    ...facilityLayer.closureOccurrences,
    ...facilityLayer.playEventOccurrences,
  ];

  return (
    <div className="min-h-0">
      <div className="contents">
        {!embedded ? (
          <PageHeader
            title="Calendar"
            subtitle={`${currentClub.name} — facility schedule`}
            actions={<ScheduleViewDropdown value={viewMode} onChange={setViewMode} />}
            subheader={headerSubheader}
            help={
              <PageFeatureHelp
                title="Facility calendar"
                tours={[
                  {
                    tourKey: 'club-calendar',
                    label: 'Tour this page',
                    description: 'Switch views and filter court hire, closures, and play events.',
                  },
                ]}
              >
                <p>
                  See everything happening on your courts — court hire bookings, closures, and play
                  events — in one schedule.
                </p>
              </PageFeatureHelp>
            }
            helpAnchorId={CLUB_PAGE_TOUR_STEP_IDS.CALENDAR_GUIDE}
          />
        ) : null}

        <div className="w-full">
          {facilityLayer.loading ? (
            <div className="flex min-h-[240px] items-center justify-center" aria-busy="true">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
            </div>
          ) : viewMode === 'day' ? (
            <DayCourtScheduleView
              date={selectedDayDate}
              onDateChange={handleDayDateChange}
              courts={facilityLayer.clubCourts}
              occurrences={dayOccurrences}
              academyId={currentClub.id}
              editable={false}
              hideHeader
              sourceTypeFilters={activeSourceFilters}
              onOccurrenceClick={(occ) => {
                if (occ.sourceType === 'play_event') {
                  handleScheduleOccurrenceClick(occ);
                }
              }}
            />
          ) : (
            <AcademyScheduleCalendar
              hideHeader
              lessons={[]}
              terms={[]}
              overrides={[]}
              lessonSessions={[]}
              courtNamesById={courtNamesById}
              viewMode={viewMode === 'list' ? 'list' : viewMode}
              onViewModeChange={(v) => setViewMode(v)}
              currentMonth={calendarMonth}
              onCurrentMonthChange={setCalendarMonth}
              weekStart={calendarWeekStart}
              onWeekStartChange={setCalendarWeekStart}
              extraOccurrences={[
                ...facilityLayer.occupancyLessons,
                ...extraOccurrences,
              ]}
              onScheduleOccurrenceClick={handleScheduleOccurrenceClick}
              sourceTypeFilters={activeSourceFilters}
              academyIdForAgeGroups={currentClub.id}
              loading={facilityLayer.loading}
              listContent={
                <div className="space-y-6">
                  {isSourceActive('lesson') && facilityLayer.occupancyLessons.length === 0 ? (
                    <DashboardEmptyStateCard>
                      <DashboardEmptyStateIcon icon={CalendarDays} />
                      <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                        No lessons scheduled
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Lesson sessions appear here when scheduled on club courts.
                      </p>
                    </DashboardEmptyStateCard>
                  ) : null}

                  {isSourceActive('court_hire') ? (
                    <section>
                      <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                        Court hire
                      </h3>
                      {listCourtHire.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No court hire bookings in this period.
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {listCourtHire.map((occ) => (
                            <li
                              key={occ.lessonId}
                              className="rounded-lg border border-border bg-card p-3 text-sm"
                            >
                              <p className="font-medium text-gray-900 dark:text-white">
                                {occ.lessonName}
                              </p>
                              <p className="text-gray-600 dark:text-gray-400">
                                {format(occ.date, 'EEE, MMM d')} ·{' '}
                                {formatTimeHHMM(occ.startTime)} – {formatTimeHHMM(occ.endTime)}
                                {occ.courtLabel ? ` · ${occ.courtLabel}` : ''}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                      <Link
                        to="/admin/court-hire"
                        className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                      >
                        Manage court hire
                      </Link>
                    </section>
                  ) : null}

                  {isSourceActive('closure') ? (
                    <section>
                      <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                        Closures
                      </h3>
                      {listClosures.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No closures in this period.
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {listClosures.map((occ) => (
                            <li
                              key={occ.lessonId}
                              className="rounded-lg border border-border bg-card p-3 text-sm"
                            >
                              <p className="font-medium text-gray-900 dark:text-white">Closure</p>
                              <p className="text-gray-600 dark:text-gray-400">
                                {format(occ.date, 'EEE, MMM d')} ·{' '}
                                {formatTimeHHMM(occ.startTime)} – {formatTimeHHMM(occ.endTime)}
                                {occ.courtLabel ? ` · ${occ.courtLabel}` : ''}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  ) : null}

                  {isSourceActive('play_event') ? (
                    <section>
                      <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                        Play events
                      </h3>
                      {listPlayEvents.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No play events in this period.
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {listPlayEvents.map((occ) => (
                            <li
                              key={occ.lessonId}
                              className="rounded-lg border border-border bg-card p-3 text-sm"
                            >
                              <p className="font-medium text-gray-900 dark:text-white">
                                {occ.lessonName}
                              </p>
                              <p className="text-gray-600 dark:text-gray-400">
                                {format(occ.date, 'EEE, MMM d')} ·{' '}
                                {formatTimeHHMM(occ.startTime)} – {formatTimeHHMM(occ.endTime)}
                                {occ.courtLabel ? ` · ${occ.courtLabel}` : ''}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                      <Link
                        to="/admin/play-events"
                        className="mt-3 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                      >
                        Manage play events
                      </Link>
                    </section>
                  ) : null}
                </div>
              }
              emptyContent={
                allOccurrences.length === 0 ? (
                  <DashboardEmptyStateCard>
                    <DashboardEmptyStateIcon icon={CalendarDays} />
                    <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                      No schedule items
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Adjust filters or pick another date range.
                    </p>
                  </DashboardEmptyStateCard>
                ) : undefined
              }
            />
          )}
        </div>
      </div>

      <PlayEventScheduleDetailDrawer
        isOpen={!!playEventDetailOccurrence}
        onClose={() => setPlayEventDetailOccurrence(null)}
        occurrence={playEventDetailOccurrence}
        organizationId={currentClub?.organizationId}
        onAfterAction={() => void facilityLayer.refresh()}
      />
    </div>
  );
}
